import { Component, onMount, createSignal } from "solid-js";
import { useSearchParams, useNavigate } from "@solidjs/router";
import { Spinner } from "../components/ui";

/**
 * Handles both hops of the GitHub App install flow (FLOW-518):
 *
 *  1. GitHub redirects here after the user installs or cancels the App, with
 *     ?installation_id=X&setup_action=install&state=<nonce>. We hand the
 *     claimed installation_id and the FLOW-501 CSRF nonce off to the
 *     backend's /authorize hop via a full-page navigation — the backend
 *     verifies the user actually owns that installation (against GitHub's
 *     user-delegated /user/installations) before registering it.
 *  2. The backend redirects back here with ?verified=1 once registration
 *     succeeds. We then consume the return URL stashed in localStorage and
 *     navigate the user back to wherever the install flow started.
 */
export const GitHubAppCallbackPage: Component = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = createSignal("");

  const returnToOrigin = (suffix: string) => {
    const returnUrl = localStorage.getItem("github_app_return_url") || "/board";
    localStorage.removeItem("github_app_return_url");
    navigate(`${returnUrl}${returnUrl.includes("?") ? "&" : "?"}${suffix}`);
  };

  onMount(() => {
    try {
      const rawVerified = searchParams.verified;
      const verified = Array.isArray(rawVerified)
        ? rawVerified[0]
        : rawVerified;
      if (verified === "1") {
        returnToOrigin("github_setup=success");
        return;
      }

      const rawInstallationId = searchParams.installation_id;
      const installationId = Array.isArray(rawInstallationId)
        ? rawInstallationId[0]
        : rawInstallationId;
      const rawSetupAction = searchParams.setup_action;
      const setupAction = Array.isArray(rawSetupAction)
        ? rawSetupAction[0]
        : rawSetupAction;
      // GitHub passes back the state param we sent via the backend initiate
      // endpoint (FLOW-501). Forward it to /authorize for nonce validation.
      const rawState = searchParams.state;
      const installState = Array.isArray(rawState) ? rawState[0] : rawState;

      if (!installationId) {
        // User may have cancelled - redirect back with a flag
        returnToOrigin("github_setup=cancelled");
        return;
      }

      // setup_action=request means the user requested access but org admin hasn't approved
      if (setupAction === "request") {
        returnToOrigin("github_setup=requested");
        return;
      }

      // Hand off to the backend's authorize hop (FLOW-518). This MUST be a
      // full-page navigation, not a fetch/api.post — it carries the user
      // through GitHub's OAuth consent screen, which an XHR cannot do.
      const params = new URLSearchParams({
        installation_id: installationId,
        state: installState ?? "",
      });
      window.location.href = `/api/github/app/authorize?${params.toString()}`;
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Failed to complete GitHub App installation",
      );
    }
  });

  return (
    <div class="flex min-h-screen items-center justify-center bg-paper p-4">
      <div class="w-full max-w-md text-center">
        {error() ? (
          <div>
            <div class="mb-4 text-signal-red">
              <svg
                class="mx-auto h-12 w-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
              </svg>
            </div>
            <h1 class="mb-2 text-lg font-semibold text-ink">
              Installation failed
            </h1>
            <p class="mb-6 text-sm text-ink-3">{error()}</p>
            <button
              class="inline-flex items-center justify-center rounded focus-ring bg-ink px-4 py-2 text-sm font-medium text-paper transition-colors hover:bg-ink-2"
              onClick={() => navigate("/board")}
            >
              Go to Board
            </button>
          </div>
        ) : (
          <div>
            <Spinner size="lg" />
            <p class="mt-4 text-sm text-ink-3">
              Completing GitHub App installation...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
